/**
 * 포탈 공통 라디오 스타일. RadioV2 와 theme/overrides/Radio 에서 같이 사용.
 *
 * 선택돼도 테두리는 회색 — 선택 표시는 안쪽 점.
 */

const SIZE = 20;
const DOT = 12;

// hover/pressed 는 라디오가 아니라 행에 걸림. 행은 MUI 라벨이거나, 직접 만든
// 사용처가 붙인 .v2rd-hover-target. 마지막 `&:${state}` 는 행이 없을 때의 폴백.
const rowState = (state: 'hover' | 'active', extra = '') =>
  [
    `.MuiFormControlLabel-root:${state} &`,
    `.v2rd-hover-target:${state} &`,
    `&:${state}`,
  ].map((sel) => `${sel}:not(.Mui-disabled)${extra}`);

// 자손은 셀렉터마다 붙일 것 — 콤마로 먼저 이으면 마지막 하나에만 걸림.
const on = (selectors: string[], target: string) =>
  selectors.map((sel) => `${sel} ${target}`).join(', ');

const HOVER = rowState('hover');
const HOVER_CHECKED = rowState('hover', '.Mui-checked');
const PRESS = rowState('active');
const PRESS_CHECKED = rowState('active', '.Mui-checked');
const PRESS_UNCHECKED = rowState('active', ':not(.Mui-checked)');
const FOCUS = ['&.Mui-focusVisible:not(.Mui-disabled)'];

// 선택/미선택 마크업 동일. 미선택도 pressed 에서 점이 보여야 해서 dot 은 항상 유지.
export const RADIO_DS_ICON = (
  <span className="v2rd-root">
    <span className="v2rd-body">
      <span className="v2rd-dot" />
    </span>
  </span>
);

export const radioDsRootStyles = {
  // 패딩(hit-area)은 MUI 기본 9px 유지 — 줄이면 기존 사용처 간격이 틀어짐.
  '&:hover': { backgroundColor: 'transparent' },
  '&.Mui-focusVisible': { outline: 'none' },
  // disableRipple 만으로는 안 꺼짐(MUI 7). 38px 라 halo 보다 넓게 번짐.
  '& .MuiTouchRipple-root': { display: 'none' },

  '& .v2rd-root': {
    position: 'relative',
    display: 'inline-flex',
    width: SIZE,
    height: SIZE,
    alignItems: 'center',
    justifyContent: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      inset: -2,
      borderRadius: 999,
      background: 'transparent',
      pointerEvents: 'none',
      zIndex: 0,
    },
  },
  '& .v2rd-body': {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: SIZE,
    height: SIZE,
    borderRadius: 999,
    boxSizing: 'border-box',
    background: 'var(--dk-bg, #FFF)',
    border: '1px solid var(--dk-line-strong, #B7BBC2)',
  },
  '& .v2rd-dot': {
    width: DOT,
    height: DOT,
    borderRadius: 999,
    flexShrink: 0,
    background: 'transparent',
  },

  '&.Mui-checked:not(.Mui-disabled) .v2rd-dot': { background: '#06C' },
  '&.Mui-disabled .v2rd-body': {
    background: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: 'var(--dk-line-strong, #D1D8E2)',
  },
  '&.Mui-checked.Mui-disabled .v2rd-dot': { background: 'var(--dk-bg-strong, #D1D8E2)' },

  // DS 에 focus 상태가 없어 키보드 포커스는 hover 표시를 그대로 사용(접근성).
  [on([...HOVER, ...FOCUS], '.v2rd-root::before')]: { background: 'var(--dk-accent-tint, #C2DAF3)' },
  [on([...HOVER, ...FOCUS], '.v2rd-body')]: {
    background: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: 'var(--dk-accent, #06C)',
  },
  [on(HOVER_CHECKED, '.v2rd-dot')]: { background: '#5C9DDE' },

  // 누르는 동안 halo 제거 — 남겨두면 링과 겹쳐 두 겹으로 보임.
  [on(PRESS, '.v2rd-root::before')]: { display: 'none' },
  [on(PRESS, '.v2rd-body')]: { background: 'var(--dk-bg-subtle, #F9FAFB)', borderColor: 'var(--dk-accent, #06C)' },
  [on(PRESS_CHECKED, '.v2rd-dot')]: { background: 'var(--dk-accent-tint, #C2DAF3)' },
  [on(PRESS_UNCHECKED, '.v2rd-dot')]: { background: '#5C9DDE' },
};
